// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Curry from "rescript/lib/es6/curry.js";
import * as React from "react";
import * as Caml_option from "rescript/lib/es6/caml_option.js";
import * as Core__Option from "@rescript/core/src/Core__Option.mjs";
import * as RelayRouter__Context from "./RelayRouter__Context.mjs";
import * as RelayRouter__Bindings from "./RelayRouter__Bindings.mjs";
import * as ReactRouterJs from "./vendor/react-router.js";

function useSetQueryParams(parseQueryParams, applyQueryParams) {
  var router = RelayRouter__Context.useRouterContext();
  var setQueryParamsFn = React.useCallback((function (param) {
          var queryParams = param.removeNotControlledParams ? RelayRouter__Bindings.QueryParams.make() : RelayRouter__Bindings.QueryParams.parse(param.currentSearch);
          param.applyQueryParams(queryParams);
          if (param.shallow) {
            router.markNextNavigationAsShallow();
          }
          if (param.navigationMode_ === "Push") {
            router.history.push(RelayRouter__Bindings.QueryParams.toString(queryParams));
            return ;
          }
          router.history.replace(RelayRouter__Bindings.QueryParams.toString(queryParams));
        }), [
        router,
        applyQueryParams
      ]);
  return React.useMemo((function () {
                return function (setter, onAfterParamsSet, navigationMode_Opt, removeNotControlledParamsOpt, shallowOpt) {
                  var navigationMode_ = navigationMode_Opt !== undefined ? navigationMode_Opt : "Push";
                  var removeNotControlledParams = removeNotControlledParamsOpt !== undefined ? removeNotControlledParamsOpt : true;
                  var shallow = shallowOpt !== undefined ? shallowOpt : true;
                  var match = router.history.location;
                  var search = match.search;
                  var newParams = setter(parseQueryParams(RelayRouter__Bindings.QueryParams.parse(search)));
                  if (onAfterParamsSet !== undefined) {
                    onAfterParamsSet(newParams);
                  }
                  setQueryParamsFn({
                        applyQueryParams: (function (none) {
                            return Curry._2(applyQueryParams, none, newParams);
                          }),
                        currentSearch: search,
                        navigationMode_: navigationMode_,
                        removeNotControlledParams: removeNotControlledParams,
                        shallow: shallow
                      });
                };
              }), [
              parseQueryParams,
              applyQueryParams,
              router
            ]);
}

function useMakeLinkWithPreservedPath(parseQueryParams, applyQueryParams) {
  var router = RelayRouter__Context.useRouterContext();
  return React.useMemo((function () {
                return function (makeNewQueryParams) {
                  var $$location = router.history.location;
                  var newQueryParams = makeNewQueryParams(parseQueryParams(RelayRouter__Bindings.QueryParams.parse($$location.search)));
                  var queryParams = RelayRouter__Bindings.QueryParams.parse($$location.search);
                  applyQueryParams(queryParams, newQueryParams);
                  return $$location.pathname + RelayRouter__Bindings.QueryParams.toString(queryParams);
                };
              }), [
              router,
              parseQueryParams,
              applyQueryParams
            ]);
}

function matchPath(prim0, prim1) {
  return Caml_option.nullable_to_opt(ReactRouterJs.matchPath(prim0, prim1));
}

function matchPathWithOptions(prim0, prim1) {
  return Caml_option.nullable_to_opt(ReactRouterJs.matchPath(prim0, prim1));
}

var extractDisposables = (function extractDisposables_(s, disposables = [], seen = new Set()) {
  if (s == null || seen.has(s)) {
    return disposables;
  }
  
  seen.add(s);

  if (Array.isArray(s)) {
    s.forEach(function (o) {
      extractDisposables_(o, disposables, seen);
    });
    return disposables;
  }

  if (typeof s === "object") {
    if (s.hasOwnProperty("dispose") && typeof s.dispose === "function") {
      disposables.push(s.dispose);
    }

    Object.keys(s).forEach(function (key) {
      var o = s[key];
      extractDisposables_(o, disposables, seen);
    });
  }

  return disposables;
});

var supportsRequestIdleCallback = import.meta.env.SSR ? false : (window != null && window.requestIdleCallback != null);

function runCallback(cb, timeout) {
  if (supportsRequestIdleCallback) {
    var id = window.requestIdleCallback(cb, timeout !== undefined ? ({
              timeout: timeout
            }) : undefined);
    return (function () {
              window.cancelIdleCallback(id);
            });
  }
  var id$1 = setTimeout(cb, 1);
  return (function () {
            clearTimeout(id$1);
          });
}

function runAtPriority(cb, priority) {
  if (import.meta.env.SSR) {
    return ;
  }
  switch (priority) {
    case "High" :
        cb();
        return ;
    case "Default" :
        return runCallback(cb, 2000);
    case "Low" :
        return runCallback(cb, undefined);
    
  }
}

var context = React.createContext(function (_cb) {
      
    });

var make = context.Provider;

var Provider = {
  make: make
};

function use() {
  return React.useContext(context);
}

function parseRoute(routeKind) {
  switch (routeKind.TAG) {
    case "QueryParams" :
        var parseQueryParams = routeKind.parseQueryParams;
        var routePattern = routeKind.routePattern;
        return function (route, exactOpt) {
          var exact = exactOpt !== undefined ? exactOpt : false;
          var match = route.split("?");
          if (match.length !== 2) {
            return ;
          }
          var pathName = match[0];
          var search = match[1];
          return Core__Option.map(Caml_option.nullable_to_opt(ReactRouterJs.matchPath({
                              path: routePattern,
                              end: exact
                            }, pathName)), (function (param) {
                        return parseQueryParams(RelayRouter__Bindings.QueryParams.parse(search));
                      }));
        };
    case "PathAndQueryParams" :
        var parseQueryParams$1 = routeKind.parseQueryParams;
        var routePattern$1 = routeKind.routePattern;
        return function (route, exactOpt) {
          var exact = exactOpt !== undefined ? exactOpt : false;
          var match = route.split("?");
          if (match.length !== 2) {
            return ;
          }
          var pathName = match[0];
          var search = match[1];
          return Core__Option.map(Caml_option.nullable_to_opt(ReactRouterJs.matchPath({
                              path: routePattern$1,
                              end: exact
                            }, pathName)), (function (param) {
                        var queryParams = parseQueryParams$1(RelayRouter__Bindings.QueryParams.parse(search));
                        return [
                                param.params,
                                queryParams
                              ];
                      }));
        };
    case "PathParams" :
        var routePattern$2 = routeKind.routePattern;
        return function (route, exactOpt) {
          var exact = exactOpt !== undefined ? exactOpt : false;
          return Core__Option.map(Caml_option.nullable_to_opt(ReactRouterJs.matchPath({
                              path: routePattern$2,
                              end: exact
                            }, route)), (function (param) {
                        return param.params;
                      }));
        };
    
  }
}

var RouterTransitionContext = {
  context: context,
  use: use,
  Provider: Provider
};

export {
  useSetQueryParams ,
  useMakeLinkWithPreservedPath ,
  matchPath ,
  matchPathWithOptions ,
  extractDisposables ,
  runAtPriority ,
  RouterTransitionContext ,
  parseRoute ,
}
/* supportsRequestIdleCallback Not a pure module */
